import { Component, ElementRef, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormGroupDirective, ReactiveFormsModule, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

import { VERSION } from '../../../environments/version';
import { LoggerService } from '../../shared/services/logger.service';
import { NotifyService } from '../../shared/services/notify.service';
import { Preference, ProviderEnum } from '../../shared/models/preference';
import { PreferenceService } from '../../shared/services/preference.service';
import { AuthService } from '../../shared/services/auth.service';
import { NetworkService } from '../../shared/services/network.service';
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field';
import { AsyncPipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

/** Error when invalid control is dirty, touched, or submitted. */
export class PrefErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | null): boolean {
    const isSubmitted = form && form.submitted;

    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

interface PrefForm {
  userId: FormControl<string>;
  name: FormControl<string>;
  email: FormControl<string>;
  auditoria: FormControl<boolean>;
  avisos: FormControl<boolean>;
  providerPassword: FormControl<boolean>;
  providerGoogle: FormControl<boolean>;
  providerFacebook: FormControl<boolean>;
  showImage: FormControl<boolean>;
}

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  imports: [
    AsyncPipe,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatTooltipModule,
    ReactiveFormsModule,
  ],
})
export class ProfileComponent implements OnInit, OnDestroy {
  public loading = true;
  public matcher = new PrefErrorStateMatcher();
  public prefFormGroup: FormGroup<PrefForm>;
  private prefSub: Subscription;
  private initialValues: Partial<Preference>;
  public defaultProvider: string;
  public pref: Preference;
  public providerEnum = ProviderEnum;
  public canRemoveProfile: boolean = false;
  public isStandAlone = this.netSvc.isStandAlone;
  public version = VERSION;
  public image$ = this.prefs.image;

  @ViewChild('removeProfileModal') removeProfileModal: ElementRef;
  subOnline: Subscription;
  online: boolean;

  constructor(
    private auth: AuthService,
    private logger: LoggerService,
    private modalService: NgbModal,
    private netSvc: NetworkService,
    public prefs: PreferenceService,
    private notifySvc: NotifyService
  ) {
    this.prefFormGroup = new FormGroup<PrefForm>({
      userId: new FormControl(),
      name: new FormControl(),
      email: new FormControl(),
      auditoria: new FormControl(),
      avisos: new FormControl(),
      providerPassword: new FormControl(),
      providerGoogle: new FormControl(),
      providerFacebook: new FormControl(),
      showImage: new FormControl(),
    });
  }

  ngOnInit() {
    this.loading = true;
    this.subOnline = this.netSvc.online.subscribe(online => {
      this.online = online;
    });
    this.prefSub = this.prefs.items.subscribe({
      next: async pref => {
        if (pref) {
          this.pref = pref;
          this.prefFormGroup = new FormGroup<PrefForm>({
            userId: new FormControl({ value: pref.userId, disabled: true }),
            name: new FormControl(pref.name, [
              Validators.required,
              Validators.minLength(PreferenceService.nameMin),
              Validators.maxLength(PreferenceService.nameMax),
            ]),
            email: new FormControl({ value: pref.email, disabled: true }),
            auditoria: new FormControl({ value: pref.auditoria ?? false, disabled: false }),
            avisos: new FormControl({
              value: (await this.notifySvc.getCurrentPermission()) ?? false,
              disabled: !(await this.notifySvc.isNotificationSupported()),
            }),
            providerPassword: new FormControl({
              value: pref.providerPassword ?? false,
              disabled: pref.provider === ProviderEnum.Password,
            }),
            providerGoogle: new FormControl({
              value: pref.providerGoogle ?? false,
              disabled: pref.provider === ProviderEnum.Google,
            }),
            providerFacebook: new FormControl({
              value: pref.providerFacebook ?? false,
              disabled: pref.provider === ProviderEnum.Facebook || pref.provider === 'facebook',
            }),
            showImage: new FormControl(pref.showImage),
          });
          this.initialValues = this.prefFormGroup.value;
          if (!pref.grupos || Object.keys(pref.grupos).length == 0) {
            this.canRemoveProfile = true;
          }
          this.loading = false;
        } else {
          this.loading = true;
        }
      },
      complete: () => {
        this.pref = undefined;
      },
    });
  }

  ngOnDestroy(): void {
    if (this.prefSub) {
      this.prefSub.unsubscribe();
    }
    if (this.subOnline) {
      this.subOnline.unsubscribe();
    }
  }

  public handleMissingImage(event: Event) {
    (event.target as HTMLImageElement).style.display = 'none';
  }

  getNameError() {
    const nameControl = this.name;
    if (nameControl.hasError('required')) {
      return 'Um nome é obrigatório';
    }
    if (nameControl.hasError('minlength')) {
      return `Nome deve possuir pelo menos ${PreferenceService.nameMin} caracteres`;
    }
    if (nameControl.hasError('maxlength')) {
      return `Nome deve possuir no máximo ${PreferenceService.nameMax} caracteres`;
    }
  }

  /**
   * Elimina espaços antes e depois do texto do nome
   */
  private trimName(): void {
    const originalName = this.prefFormGroup.get('name').value;
    const name = originalName.trim();
    if (name !== originalName) {
      this.prefFormGroup.patchValue({ name: name });
      this.logger.debug(`Profile:trimName:/${originalName}/${name}/`);
    }
  }

  submit() {
    this.trimName();
    if (this.prefFormGroup.valid) {
      this.loading = true;
      this.prefs
        .update(this.prefFormGroup.value)
        .then(() => {
          this.logger.debug('Profile:Submit:Ok');
          this.notifySvc.showFastInfo('Preferências gravadas com sucesso');
        })
        .finally(() => {
          this.loading = false;
        })
        .catch(err => {
          this.logger.error({ 'Profile:Submit': err });
        });
    } else {
      this.notifySvc.showWarning('Falha ao enviar os dados, campos incorretos');
    }
  }

  reset() {
    this.prefFormGroup.patchValue(this.initialValues);
    this.prefFormGroup.markAsPristine();
  }

  get name() {
    return this.prefFormGroup.get('name');
  }
  get auditoria() {
    return this.prefFormGroup.get('auditoria');
  }
  get providerEmail() {
    return this.prefFormGroup.get('providerEmail');
  }

  public linkLogin(event: MatSlideToggleChange, providerId: string) {
    if (event.checked) {
      this.auth
        .linkProvider(providerId)
        .then(msg => {
          this.notifySvc.showInfo(msg);
        })
        .catch(err => {
          event.source.toggle();
          this.notifySvc.showWarning(err);
        });
    } else {
      this.auth
        .unlinkProvider(providerId)
        .then(msg => {
          this.notifySvc.showInfo(msg);
        })
        .catch(err => {
          event.source.toggle();
          this.notifySvc.showWarning(err);
        });
    }
  }

  public open(content: ElementRef | TemplateRef<ElementRef>) {
    this.modalService.dismissAll();
    this.modalService.open(content, { ariaLabelledBy: 'modal-dialog' }).result.then(
      result => {
        this.logger.debug({ 'Group:DialogClosed': result });
      },
      () => {
        this.logger.debug('Group:DialogDismissed');
      }
    );
  }

  public openRemoveForm(): void {
    this.open(this.removeProfileModal);
  }

  public removeProfile(): void {
    this.auth
      .delete()
      .then(message => {
        this.logger.debug('Group:removeProfile:Ok');
        this.notifySvc.showResponse(message);
        this.modalService.dismissAll();
      })
      .catch(err => {
        this.logger.debug({ 'Group:removeProfile': err });
        this.notifySvc.backLater();
      });
  }

  public dismissModal(): void {
    this.modalService.dismissAll();
    this.loading = false;
  }

  public getNotificationPermission(): Promise<boolean> {
    return this.notifySvc.getCurrentPermission();
  }
}
